import React, { useMemo } from 'react';

const getContrastColor = (hexcolor) => {
    if (!hexcolor || hexcolor.startsWith('url')) return 'white';
    const r = parseInt(hexcolor.substring(1, 3), 16);
    const g = parseInt(hexcolor.substring(3, 5), 16);
    const b = parseInt(hexcolor.substring(5, 7), 16);
    const yiq = ((r * 299) + (g * 587) + (b * 114)) / 1000;
    return (yiq >= 128) ? '#111827' : '#ffffff';
};

export default function PlannerWebsiteRenderer({ sections = [], theme = {}, planner = {}, onInquire }) {
    const globalBg = theme.backgroundColor || '#ffffff';
    const primaryColor = theme.primaryColor || '#db2777';
    const radius = theme.borderRadius || '0.5rem';
    const fontMain = theme.fontFamily || "'Inter', sans-serif";

    const renderSection = (section) => {
        if (!section.enabled) return null;
        const { type, content, id } = section;

        // Dynamic styling for the specific section
        const sectionStyle = {
            backgroundColor: content.bgColor || globalBg,
            color: content.textColor || (content.bgColor ? getContrastColor(content.bgColor) : getContrastColor(globalBg)),
            padding: content.padding || '5rem 1.25rem'
        };

        switch (type) {
            case 'hero':
                return (
                    <section 
                        key={id} 
                        id="home"
                        className="relative flex overflow-hidden transition-all duration-500"
                        style={{ 
                            ...sectionStyle, 
                            minHeight: content.height || '100vh', 
                            alignItems: content.verticalAlign || 'center', 
                            justifyContent: content.horizontalAlign === 'left' ? 'flex-start' : content.horizontalAlign === 'right' ? 'flex-end' : 'center' 
                        }}
                    >
                        {content.imageUrl && (
                            <>
                                <img src={content.imageUrl} alt="Hero Background" className="absolute inset-0 w-full h-full object-cover" />
                                <div className="absolute inset-0 bg-black" style={{ opacity: content.overlayOpacity !== undefined ? content.overlayOpacity : 0.4 }}></div>
                            </>
                        )}
                        <div className={`relative z-10 w-full max-w-5xl p-6 md:p-12 animate-fade-in ${content.horizontalAlign === 'left' ? 'text-left' : content.horizontalAlign === 'right' ? 'text-right' : 'text-center'}`}>
                            <h1 className="text-5xl md:text-8xl font-black mb-6 tracking-tighter leading-none" style={{ color: content.textColor || (content.imageUrl ? '#ffffff' : primaryColor) }}>
                                {content.title || "Elite Events"}
                            </h1>
                            <p className="text-xl md:text-3xl mb-10 font-medium opacity-90 max-w-2xl inline-block" style={{ color: content.textColor || (content.imageUrl ? '#f3f4f6' : '#4b5563') }}>
                                {content.subtitle}
                            </p>
                            <div className="flex flex-wrap gap-4 justify-inherit">
                                {content.buttonText && (
                                    <button 
                                        onClick={() => {
                                            const target = document.getElementById('contact');
                                            if (target) target.scrollIntoView({ behavior: 'smooth' });
                                        }}
                                        className="px-10 py-4 font-black uppercase tracking-widest text-sm transition-all hover:scale-105 hover:shadow-xl active:scale-95" 
                                        style={{ backgroundColor: primaryColor, color: getContrastColor(primaryColor), borderRadius: radius }}
                                    >
                                        {content.buttonText}
                                    </button>
                                )}
                            </div>
                        </div>
                    </section>
                );

            case 'about':
                return (
                    <section key={id} id="about" style={sectionStyle} className="border-b border-gray-100/5">
                        <div className="container mx-auto max-w-6xl">
                            <div className={`flex items-center gap-16 flex-col md:flex-row ${content.layout === 'right' ? 'md:flex-row-reverse' : ''}`}>
                                {content.imageUrl && (
                                    <div className="w-full md:w-1/2 relative group">
                                        <div className="absolute -inset-4 opacity-20 blur-2xl transition duration-500 group-hover:opacity-40" style={{ backgroundColor: primaryColor }}></div>
                                        <img src={content.imageUrl} alt="About Us" className="relative shadow-2xl w-full h-auto object-cover aspect-[4/5]" style={{ borderRadius: radius }} />
                                    </div>
                                )}
                                <div className={`w-full ${content.imageUrl ? 'md:w-1/2' : 'max-w-3xl mx-auto text-center'} space-y-8`}>
                                    <h2 className="text-4xl md:text-5xl font-black tracking-tight" style={{ color: primaryColor }}>{content.heading || "The Visionary"}</h2>
                                    <div className="prose prose-lg leading-relaxed whitespace-pre-wrap font-medium opacity-80" style={{ color: 'inherit' }}>
                                        {content.text}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>
                );

            case 'services':
                return (
                    <section key={id} id="services" style={sectionStyle} className="relative">
                        <div className="container mx-auto max-w-7xl">
                            <div className="text-center mb-20">
                                <h2 className="text-4xl md:text-6xl font-black mb-4 tracking-tighter" style={{ color: primaryColor }}>{content.heading || 'Our Services'}</h2>
                                {content.subtitle && <p className="text-xl opacity-70 max-w-2xl mx-auto font-medium">{content.subtitle}</p>}
                            </div>
                            <div className="grid gap-10 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
                                {(content.packages || []).map((pkg, idx) => (
                                    <div key={idx} className="bg-white shadow-xl border border-gray-100 flex flex-col transition-all hover:-translate-y-2 hover:shadow-2xl group" style={{ borderRadius: `calc(${radius} * 1.5)` }}>
                                        {pkg.imageUrl && <img src={pkg.imageUrl} alt={pkg.title} className="w-full h-64 object-cover" style={{ borderTopLeftRadius: `calc(${radius} * 1.5)`, borderTopRightRadius: `calc(${radius} * 1.5)` }} />}
                                        <div className="p-10 flex-1 flex flex-col">
                                            <h3 className="text-2xl font-black text-gray-900 mb-2 group-hover:text-pink-600 transition-colors">{pkg.title}</h3>
                                            <div className="flex items-baseline gap-1 mb-6">
                                                <span className="text-3xl font-black" style={{ color: primaryColor }}>
                                                    {pkg.price ? `$${pkg.price}` : 'Custom Quote'}
                                                </span>
                                                {pkg.price && <span className="text-gray-400 text-sm font-bold uppercase tracking-widest">Starting</span>}
                                            </div>
                                            <p className="text-gray-600 mb-8 flex-1 leading-relaxed text-sm font-medium">{pkg.description}</p>
                                            
                                            {pkg.features && pkg.features.length > 0 && (
                                                <ul className="mb-8 space-y-3">
                                                    {pkg.features.map((f, i) => (
                                                        <li key={i} className="flex items-center gap-3 text-xs font-bold text-gray-500 uppercase tracking-wide">
                                                            <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: primaryColor }}></span> {f}
                                                        </li>
                                                    ))}
                                                </ul>
                                            )}

                                            <button 
                                                onClick={() => onInquire && onInquire(pkg.title)} 
                                                className="mt-auto w-full py-4 font-black uppercase tracking-[0.2em] text-xs transition-all text-white shadow-lg hover:brightness-110 active:scale-95" 
                                                style={{ backgroundColor: primaryColor, borderRadius: radius }}
                                            >
                                                Inquire Now
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </section>
                );

            case 'gallery':
                return (
                    <div key={id} id="portfolio" style={sectionStyle}>
                        <div className="container mx-auto max-w-7xl">
                            <h2 className="text-4xl md:text-5xl font-black text-center mb-16 tracking-tight" style={{ color: primaryColor }}>{content.heading || 'Portfolio'}</h2>
                            <div className="gap-6 space-y-6 columns-1 sm:columns-2 lg:columns-3">
                                {(content.images || []).map((img, idx) => (
                                    <div key={idx} className="break-inside-avoid overflow-hidden group relative shadow-lg hover:shadow-2xl transition-all duration-500" style={{ borderRadius: radius }}>
                                        <img src={img.url} alt={`Gallery ${idx}`} className="w-full h-auto object-cover transform group-hover:scale-110 transition duration-700" />
                                        <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition duration-500 flex items-center justify-center p-6 backdrop-blur-[2px]">
                                            <p className="text-white text-center font-black uppercase tracking-widest text-sm translate-y-4 group-hover:translate-y-0 transition-transform duration-500">{img.caption || 'View Project'}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                );

            case 'contact':
                return (
                    <section key={id} id="contact" style={{ ...sectionStyle, backgroundColor: content.bgColor || '#111827', color: content.textColor || '#ffffff' }} className="relative overflow-hidden">
                        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-white/10 to-transparent"></div>
                        <div className="container mx-auto max-w-4xl text-center relative z-10">
                            <h2 className="text-4xl md:text-6xl font-black mb-6 tracking-tighter">{content.heading || "Let's Start Planning"}</h2>
                            <p className="text-xl mb-12 opacity-80 max-w-2xl mx-auto font-medium leading-relaxed">{content.text}</p>
                            
                            <div className="flex flex-col md:flex-row items-center gap-8 justify-center">
                                {planner?.email && (
                                    <a href={`mailto:${planner.email}`} className="group flex items-center gap-4 bg-white/5 hover:bg-white/10 px-8 py-4 rounded-2xl border border-white/10 transition-all">
                                        <span className="text-2xl">✉️</span>
                                        <div className="text-left">
                                            <span className="block text-[10px] font-black uppercase tracking-widest text-gray-400">Email Us</span>
                                            <span className="font-bold text-lg">{planner.email}</span>
                                        </div>
                                    </a>
                                )}
                                {planner?.phone && (
                                    <a href={`tel:${planner.phone}`} className="group flex items-center gap-4 bg-white/5 hover:bg-white/10 px-8 py-4 rounded-2xl border border-white/10 transition-all">
                                        <span className="text-2xl">📞</span>
                                        <div className="text-left">
                                            <span className="block text-[10px] font-black uppercase tracking-widest text-gray-400">Call Us</span>
                                            <span className="font-bold text-lg">{planner.phone}</span>
                                        </div>
                                    </a>
                                )}
                            </div>
                            
                            <div className="mt-20 pt-10 border-t border-white/10 opacity-40">
                                <p className="text-xs font-black uppercase tracking-[0.3em]">Built with Host-it! Pro</p>
                            </div>
                        </div>
                    </section>
                );

            default:
                return null;
        }
    };

    return (
        <div 
            style={{ 
                fontFamily: fontMain, 
                backgroundColor: globalBg 
            }} 
            className="min-h-full transition-colors duration-500 overflow-x-hidden"
        >
            <style dangerouslySetInnerHTML={{__html: `
                @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@700;900&family=Inter:wght@400;500;700;900&family=Montserrat:wght@900&display=swap');
                .animate-fade-in { animation: fadeIn 1s ease-out forwards; }
                @keyframes fadeIn { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
                html { scroll-behavior: smooth; }
            `}} />
            
            <main className="relative">
                {sections.length > 0 ? (
                    sections.map(section => renderSection(section))
                ) : (
                    <div className="min-h-screen flex items-center justify-center text-gray-400 italic">
                        Start adding sections to build your portfolio.
                    </div>
                )}
            </main>
        </div>
    );
}